import type { CallbackInterface } from 'recoil';
import { atom, atomFamily, DefaultValue, selector, selectorFamily, waitForAll } from 'recoil';
import type { BenchmarkConfig, BlockId, Subject } from '../types';
import type { CustomBenchmarkTypeEnum } from 'venn-api';
import { analysisSubjectQuery, blockSubjects, originalAnalysisSubjectQuery } from './subjects';
import { unsavedChangesEffect } from '../../effects/unsavedChangesEffect';
import { blockSettings } from './blockSettings';
import { assertExhaustive, hasDisabledIndividualBenchmark } from 'venn-utils';
import { isLinked } from './linked';
import { viewInputManagementVersion } from './input-management/rolloutUtils';
import type { BenchmarkInputId } from './input-management/benchmarkInput';
import {
  blockBenchmarkInput,
  benchmarkInputSubject,
  benchmarkInputIsRelative,
  benchmarkInputType,
  benchmarkInputs,
  benchmarkInputName,
} from './input-management/benchmarkInput';
import { compact } from 'lodash';
import { allBlockIdsState } from '../grid';
import { resetOnStudioReset } from '../../effects/signalEffects';

const DEFAULT_RELATIVE_VALUE = false;

export const globalBenchmarkRelative = atom<boolean>({
  key: 'globalBenchmarkRelative',
  default: DEFAULT_RELATIVE_VALUE,
  effects: [unsavedChangesEffect, resetOnStudioReset],
});

export const unlinkedBenchmarkRelative = atomFamily<boolean, BlockId>({
  key: 'unlinkedBenchmarkRelative',
  default: DEFAULT_RELATIVE_VALUE,
  effects: [unsavedChangesEffect],
});

// TODO(will, collin, hesham): IMV2 make selector read only
export const blockBenchmarkRelative = selectorFamily<boolean, BlockId>({
  key: 'blockBenchmarkRelative',
  get:
    (id) =>
    ({ get }) => {
      // Correlation does not support individual benchmarks. Ever. Even if globally linked.
      if (
        get(blockBenchmarkType(id)) === 'INDIVIDUAL' &&
        hasDisabledIndividualBenchmark(get(blockSettings(id)).customBlockType)
      ) {
        return DEFAULT_RELATIVE_VALUE;
      }

      const inputManagementVersion = get(viewInputManagementVersion);
      if (inputManagementVersion === 2) {
        const inputId = get(blockBenchmarkInput(id));
        return inputId ? get(benchmarkInputIsRelative(inputId)) : DEFAULT_RELATIVE_VALUE;
      }
      if (inputManagementVersion === 1) {
        if (get(isLinked(id))) {
          return get(globalBenchmarkRelative);
        }

        return get(unlinkedBenchmarkRelative(id));
      }
      throw assertExhaustive(inputManagementVersion);
    },
  set:
    (id) =>
    ({ get, set }, newValue) => {
      if (get(isLinked(id))) {
        return set(globalBenchmarkRelative, newValue);
      }

      if (
        get(blockBenchmarkType(id)) === 'INDIVIDUAL' &&
        hasDisabledIndividualBenchmark(get(blockSettings(id)).customBlockType)
      ) {
        // This generally shouldn't be possible except for on load. Still, we should hardcode false as the set since
        // it isn't supported when true.
        return set(unlinkedBenchmarkRelative(id), false);
      }

      return set(unlinkedBenchmarkRelative(id), newValue);
    },
});

const DEFAULT_TYPE: CustomBenchmarkTypeEnum = 'NONE';

export const globalBenchmarkType = atom<CustomBenchmarkTypeEnum>({
  key: 'globalBenchmarkType',
  default: DEFAULT_TYPE,
  effects: [unsavedChangesEffect, resetOnStudioReset],
});

export const unlinkedBenchmarkType = atomFamily<CustomBenchmarkTypeEnum, BlockId>({
  key: 'unlinkedBenchmarkType',
  default: DEFAULT_TYPE,
  effects: [unsavedChangesEffect],
});

// TODO(will, collin, hesham): IMV2 make selector read only
export const blockBenchmarkType = selectorFamily<CustomBenchmarkTypeEnum, BlockId>({
  key: 'blockBenchmarkType',
  get:
    (id) =>
    ({ get }) => {
      const inputManagementVersion = get(viewInputManagementVersion);
      if (inputManagementVersion === 2) {
        const inputId = get(blockBenchmarkInput(id));
        return inputId ? get(benchmarkInputType(inputId)) : DEFAULT_TYPE;
      }
      if (inputManagementVersion === 1) {
        return get(isLinked(id)) ? get(globalBenchmarkType) : get(unlinkedBenchmarkType(id));
      }
      throw assertExhaustive(inputManagementVersion);
    },
  set:
    (id) =>
    ({ get, set }, newValue) =>
      get(isLinked(id)) ? set(globalBenchmarkType, newValue) : set(unlinkedBenchmarkType(id), newValue),
});

const DEFAULT_SUBJECT: Subject | undefined = undefined;

export const globalBenchmarkSubject = atom<Subject | undefined>({
  key: 'globalBenchmarkSubject',
  default: DEFAULT_SUBJECT,
  effects: [unsavedChangesEffect, resetOnStudioReset],
});

export const unlinkedBenchmarkSubject = atomFamily<Subject | undefined, BlockId>({
  key: 'unlinkedBenchmarkSubject',
  default: DEFAULT_SUBJECT,
  effects: [unsavedChangesEffect],
});

export const blockBenchmarkSubjectsIMV1 = selectorFamily<Subject[], BlockId>({
  key: 'benchmarkSubjectsIMV1',
  get:
    (id) =>
    ({ get }) => {
      const benchmarkType = get(blockBenchmarkType(id));
      switch (benchmarkType) {
        case 'COMMON':
          return compact([get(blockBenchmarkSubject(id))]);
        case 'INDIVIDUAL':
          const subjects = get(blockSubjects(id));
          const analysisSubjects = get(waitForAll(subjects.map((s) => analysisSubjectQuery(s))));
          const individualBenchmarks = analysisSubjects.map((subject): Subject | undefined => subject.activeBenchmark);
          return compact([...individualBenchmarks]);
        case 'NONE':
          return [];
        default:
          throw assertExhaustive(benchmarkType);
      }
    },
});

export const blockBenchmarkSubjectsIMV2 = selectorFamily<Subject[], BlockId>({
  key: 'benchmarkSubjectsIMV2',
  get:
    (id) =>
    ({ get }) => {
      const benchmarkInputId = get(blockBenchmarkInput(id));
      if (benchmarkInputId === undefined) {
        return [];
      }

      const benchmarkType = get(benchmarkInputType(benchmarkInputId));
      switch (benchmarkType) {
        case 'COMMON':
          return compact([get(benchmarkInputSubject(benchmarkInputId))]);
        case 'INDIVIDUAL':
          const subjects = get(blockSubjects(id));
          const analysisSubjects = get(waitForAll(subjects.map((s) => analysisSubjectQuery(s))));
          const individualBenchmarks = analysisSubjects.map((subject): Subject | undefined => subject.activeBenchmark);
          return compact(individualBenchmarks);
        case 'NONE':
          return [];
        default:
          throw assertExhaustive(benchmarkType);
      }
    },
});

export const blockInputBenchmarkSubjects = selectorFamily<Subject[], BlockId>({
  key: 'blockInputBenchmarkSubjects',
  get:
    (id) =>
    ({ get }) => {
      const inputManagementVersion = get(viewInputManagementVersion);
      switch (inputManagementVersion) {
        case 1:
          return get(blockBenchmarkSubjectsIMV1(id));
        case 2:
          return get(blockBenchmarkSubjectsIMV2(id));
        default:
          throw assertExhaustive(inputManagementVersion);
      }
    },
});

// TODO(will, collin, hesham): IMV2 make selector read only
export const blockBenchmarkSubject = selectorFamily<Subject | undefined, BlockId>({
  key: 'blockBenchmarkSubject',
  get:
    (id) =>
    ({ get }) => {
      const inputManagementVersion = get(viewInputManagementVersion);
      if (inputManagementVersion === 2) {
        const inputId = get(blockBenchmarkInput(id));
        return inputId ? get(benchmarkInputSubject(inputId)) : DEFAULT_SUBJECT;
      }
      if (inputManagementVersion === 1) {
        return get(isLinked(id)) ? get(globalBenchmarkSubject) : get(unlinkedBenchmarkSubject(id));
      }
      throw assertExhaustive(inputManagementVersion);
    },
  set:
    (id) =>
    ({ get, set }, newValue) => {
      const inputManagementVersion = get(viewInputManagementVersion);
      if (inputManagementVersion === 2) {
        throw new Error("block benchmark subjects can't be set in this way as of input management V2");
      } else if (inputManagementVersion === 1) {
        get(isLinked(id)) ? set(globalBenchmarkSubject, newValue) : set(unlinkedBenchmarkSubject(id), newValue);
      }
    },
});

export const multiUnlinkedBenchmarkSubjects = selectorFamily<(Subject | undefined)[], BlockId[]>({
  key: 'multiUnlinkedBenchmarkSubjects',
  get:
    (blockIds) =>
    ({ get }) =>
      blockIds.map((blockId) => (get(isLinked(blockId)) ? undefined : get(unlinkedBenchmarkSubject(blockId)))),
  set:
    (blockIds) =>
    ({ get, set }, benchmarkSubjects) => {
      blockIds.forEach((blockId, index) => {
        if (!get(isLinked(blockId)) && benchmarkSubjects[index]) {
          set(unlinkedBenchmarkSubject(blockId), benchmarkSubjects[index]);
        }
      });
    },
});

/**
 * Provides the benchmark settings for the global configuration
 * Note that original analysis subject query is used for subject as allocation changes
 * should not effect the common benchmark
 * */
export const globalBenchmarkConfig = selector<BenchmarkConfig>({
  key: 'globalBenchmarkConfig',
  get: ({ get }) => {
    const subject = get(globalBenchmarkSubject);
    return {
      relative: get(globalBenchmarkRelative),
      type: get(globalBenchmarkType),
      subject: subject && get(originalAnalysisSubjectQuery(subject)),
    };
  },
  set: ({ set }, config) => {
    if (config instanceof DefaultValue) {
      return;
    }
    set(
      globalBenchmarkSubject,
      config.subject
        ? {
            portfolioId: config.subject.portfolio?.id,
            fundId: config.subject.portfolio ? undefined : config.subject.fund?.id,
          }
        : undefined,
    );
    set(globalBenchmarkType, config.type);
    set(globalBenchmarkRelative, config.relative);
  },
});

/**
 * Provides the benchmark settings for a given block
 * Note that original analysis subject query is used for subject as allocation changes
 * should not effect the common benchmark
 * */
// TODO(will, collin, hesham): IMV2 make selector read only
export const blockBenchmarkConfig = selectorFamily<BenchmarkConfig, BlockId>({
  key: 'blockBenchmarkConfig',
  get:
    (id) =>
    ({ get }) => {
      const subject = get(blockBenchmarkSubject(id));
      return {
        type: get(blockBenchmarkType(id)),
        relative: get(blockBenchmarkRelative(id)),
        subject: subject && get(originalAnalysisSubjectQuery(subject)),
      };
    },
  set:
    (id) =>
    ({ set }, config) => {
      if (config instanceof DefaultValue) {
        return;
      }

      set(blockBenchmarkType(id), config.type);
      set(blockBenchmarkRelative(id), config.relative);
      set(
        blockBenchmarkSubject(id),
        config.subject
          ? {
              portfolioId: config.subject.portfolio?.id,
              fundId: config.subject.portfolio ? undefined : config.subject.fund?.id,
            }
          : undefined,
      );
    },
});

/** use this in a useRecoilCallback hook to delete benchmarks */
export const onBenchmarkInputGroupDelete =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async (groupId: BenchmarkInputId) => {
    set(benchmarkInputs, (ids) => ids.filter((id) => id !== groupId));

    reset(benchmarkInputName(groupId));
    reset(benchmarkInputSubject(groupId));
    reset(benchmarkInputType(groupId));

    const blockIds = await snapshot.getPromise(allBlockIdsState);
    blockIds.forEach((blockId) =>
      set(blockBenchmarkInput(blockId), (current) => (current === groupId ? undefined : current)),
    );
  };
